import { MIS_3005_D_HOT_WATER, RULES_BUNDLE_VERSION } from "@sts/rules-data";
import { ComplianceError, ENGINE_VERSION } from "./heat-loss.js";
import type { CalcStamp, CalcWarning, Provenanced } from "./types.js";

/**
 * Index-circuit pipe sizing and system hydraulics — the piece of a heat pump
 * design that decides pipe diameter, checks water velocity, and sizes the
 * circulator. Named "index circuit" because that is the standard hydraulic
 * design term for the longest/most resistant run in the system: the run whose
 * pressure drop the circulator has to be selected against, because every
 * other (shorter/less resistant) branch will always have flow to spare once
 * the index circuit is satisfied.
 *
 * MIS 3005-D §3.8.1 does not itemise pipe sizing as a required design-pack
 * document (see `14 - Design Completeness`), but no real installation
 * proceeds without it — undersized pipework starves the heat pump of flow
 * (interacting directly with `system-water-volume.ts` and `buffer-vessel.ts`)
 * and creates the erosion/noise problems CIBSE guidance sizes pipework to
 * avoid; oversized pipework wastes money and increases system water volume
 * for no benefit. This is a first pass at that arithmetic, built from
 * published fluid-mechanics method and real manufacturer/textbook data
 * rather than MCS-specific rules (MIS 3005-D has no clause of its own for
 * this) — see the citations against each constant below.
 *
 * ---------------------------------------------------------------------------
 * THE METHOD
 * ---------------------------------------------------------------------------
 * 1. Design flow rate from the circuit's heat load and design flow/return ΔT:
 *      ṁ [kg/s] = P [kW] / (cp [kJ/(kg·K)] × ΔT [K])
 *      Q [m³/s] = ṁ / ρ
 *    This is the same energy-balance relationship `sizeCylinder`'s reheat
 *    energy and `assessBufferVessel`'s anti-cycling volume already use
 *    elsewhere in this package — no new physics, just applied to flow rate
 *    instead of stored energy. ΔT itself is a design DECISION the caller
 *    states (commonly 5-10K on a heat pump primary circuit — a smaller ΔT
 *    than a gas boiler's, because the compressor's efficiency is sensitive to
 *    return temperature), not something this module invents.
 * 2. Velocity from continuity (v = Q / cross-sectional area) and Reynolds
 *    number (Re = ρvd/μ) at that flow rate, in each supplied pipe segment.
 * 3. Darcy-Weisbach pressure drop, Δp/L = f × ρv² / (2d), with the friction
 *    factor f from:
 *      - laminar flow (Re < 2300): f = 64/Re — the exact Hagen-Poiseuille
 *        result, in every fluid mechanics text (e.g. White, "Fluid
 *        Mechanics", 4th ed., McGraw-Hill — the same source used for the
 *        roughness table below, whose own Moody chart plots this line).
 *      - turbulent/transitional flow (Re >= 2300): the Swamee-Jain (1976)
 *        explicit approximation to the implicit Colebrook-White equation,
 *        f = 0.25 / [log10(ε/(3.7d) + 5.74/Re^0.9)]², accurate to within
 *        about ±0.5% of Colebrook-White over 5,000 <= Re <= 1e8 and
 *        1e-6 <= ε/d <= 1e-2 (Swamee, P.K. and Jain, A.K., "Explicit
 *        equations for pipe-flow problems", Journal of the Hydraulics
 *        Division, ASCE, 1976). Below Re 5,000 this module still applies it
 *        (there is no universally agreed single formula in the 2,300-5,000
 *        transition zone — the Moody chart itself shows a genuine band of
 *        uncertainty there) and flags the result as approximate.
 * 4. Circulator duty = the index circuit's total pressure drop (flow leg +
 *    return leg + any stated fitting losses) at the design flow rate — by
 *    definition the worst-case resistance the pump has to overcome to
 *    deliver that flow anywhere in the system.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS CITED, WHAT IS PROVISIONAL, WHAT IS NOT ATTEMPTED
 * ---------------------------------------------------------------------------
 * CITED and used without a provisional flag (these are physical reference
 * data, not design judgement calls):
 *   - Water density and dynamic viscosity 0-80°C: CRC Handbook of Chemistry
 *     and Physics figures, as tabulated in Wikipedia's "Water (data page)"
 *     (viscosity table cites the CRC Handbook directly; density table cites
 *     Dean & Lange's Handbook of Chemistry, 1999, p.1199), fetched for this
 *     work. See `WATER_PROPERTIES_CRC` below.
 *   - Pipe roughness: White, F.M., "Fluid Mechanics", 4th ed., McGraw-Hill,
 *     Table 6.1 "Recommended Roughness Values for Commercial Ducts", fetched
 *     for this work. Copper is not itself a row in that table; drawn copper
 *     tube is conventionally in the same "drawn tubing" class as the table's
 *     Brass (0.002mm) and Plastic (0.0015mm) rows, and this module uses the
 *     0.0015mm plastic-drawn-tubing figure for it rather than inventing a
 *     copper-specific number — see `PIPE_MATERIAL_ROUGHNESS_MM`.
 *   - BS EN 1057 copper tube internal diameters (Table X and Table Y,
 *     15-54mm): MM Kembla's "Copper Tube L — EN 1057" technical datasheet,
 *     fetched for this work. Kembla is an Australian manufacturer but EN 1057
 *     is the same European dimensional standard used for UK copper tube, so
 *     its wall-thickness/outside-diameter figures apply directly. See
 *     `copperTubeInternalDiameterMm`.
 *   - Specific heat of water: reuses `MIS_3005_D_HOT_WATER.data.specificHeatKjPerKgK`
 *     (4.186 kJ/(kg·K)), the same verified constant `hot-water.ts` and
 *     `buffer-vessel.ts` already use, rather than a second hard-coded copy.
 *
 * PROVISIONAL (used only as a fallback when the caller has not stated their
 * own figure, always flagged, and refused in compliance mode until
 * confirmed) — because independent secondary sources on these specific
 * numbers disagree with each other, and this project has not read the
 * primary CIBSE Guide C text directly to settle which figure it actually
 * states:
 *   - Maximum pipe velocity, 1.0 m/s — repeatedly described in UK heat pump
 *     installer engineering guidance as "from MCS and CIBSE" for avoiding
 *     noise and erosion in domestic pipework, and consistent with the older
 *     CIBSE Guide B (1986) Table B1.13 figure reported in the same
 *     literature. `DEFAULT_MAX_VELOCITY_M_PER_S`.
 *   - Maximum pressure gradient, 300 Pa/m — secondary sources attribute
 *     different figures to CIBSE guidance for this (200 Pa/m via one
 *     reading of CIBSE Guide B1:2016 §1.A1.3's life-cycle-energy criterion;
 *     350 Pa/m via another commonly repeated figure), and 300 Pa/m is used
 *     here only as a rough provisional midpoint between them, not as either
 *     cited figure. `DEFAULT_MAX_PRESSURE_GRADIENT_PA_PER_M`.
 * A designer with the actual CIBSE Guide C table or the heat pump
 * manufacturer's own pipework guidance should supply it via
 * `maxVelocityMPerS`/`maxPressureGradientPaPerM` rather than rely on either
 * default for a design pack.
 *
 * NOT ATTEMPTED — real gaps this module does not close, flagged on every
 * result (see the "honest gaps" warnings in `calculateIndexCircuitSizing`):
 *   - Full network/branch hydraulic balancing. This sizes ONE stated run
 *     (the index circuit) as a series of pipe segments; it does not model a
 *     branched network or balance flow between parallel branches.
 *   - Component pressure losses — the heat pump's own internal head loss,
 *     plate heat exchangers, buffer vessels, zone valves, TRVs, strainers —
 *     beyond whatever the caller folds into `fittingsEquivalentLengthM`.
 *     Manufacturer data sheets state these directly; this module has none.
 *   - Minimum velocity / turbulence adequacy. Ground-loop-sizing.ts can cite
 *     a hard MIS 3005-D minimum Reynolds number (§3.7.5(h), Re >= 2500) for
 *     antifreeze ground loops; no equivalent citable minimum exists here for
 *     a primary heating circuit, so this module reports the flow regime but
 *     does not gate on a minimum.
 */

export interface FluidProperties {
  readonly densityKgPerM3: number;
  readonly dynamicViscosityPaS: number;
  readonly specificHeatKjPerKgK: number;
}

export type PipeMaterial = "copper" | "pex" | "steel" | "stainless-steel";

/**
 * Absolute roughness, mm — White, "Fluid Mechanics", 4th ed., Table 6.1. See
 * the module doc comment for why copper uses the "drawn tubing" figure
 * rather than a material-specific one.
 */
export const PIPE_MATERIAL_ROUGHNESS_MM: Readonly<Record<PipeMaterial, number>> = {
  copper: 0.0015,
  pex: 0.0015,
  steel: 0.046,
  "stainless-steel": 0.002,
};

export type CopperTubeTable = "X" | "Y";

interface CopperTubeRow {
  readonly outsideDiameterMm: number;
  readonly wallThicknessMm: number;
  readonly internalDiameterMm: number;
}

/** BS EN 1057 Table X (half-hard 15/22/28mm, hard-drawn above) — MM Kembla "Copper Tube L — EN 1057" datasheet. */
const BS_EN_1057_TABLE_X: readonly CopperTubeRow[] = [
  { outsideDiameterMm: 15, wallThicknessMm: 0.7, internalDiameterMm: 13.6 },
  { outsideDiameterMm: 22, wallThicknessMm: 0.9, internalDiameterMm: 20.2 },
  { outsideDiameterMm: 28, wallThicknessMm: 0.9, internalDiameterMm: 26.2 },
  { outsideDiameterMm: 35, wallThicknessMm: 1.2, internalDiameterMm: 32.6 },
  { outsideDiameterMm: 42, wallThicknessMm: 1.2, internalDiameterMm: 39.6 },
  { outsideDiameterMm: 54, wallThicknessMm: 1.2, internalDiameterMm: 51.6 },
];

/** BS EN 1057 Table Y (thicker-wall, coilable) — MM Kembla "Copper Tube L — EN 1057" datasheet. */
const BS_EN_1057_TABLE_Y: readonly CopperTubeRow[] = [
  { outsideDiameterMm: 15, wallThicknessMm: 1.0, internalDiameterMm: 13.0 },
  { outsideDiameterMm: 22, wallThicknessMm: 1.2, internalDiameterMm: 19.6 },
  { outsideDiameterMm: 28, wallThicknessMm: 1.2, internalDiameterMm: 25.6 },
  { outsideDiameterMm: 35, wallThicknessMm: 1.5, internalDiameterMm: 32.0 },
  { outsideDiameterMm: 42, wallThicknessMm: 1.5, internalDiameterMm: 39.0 },
  { outsideDiameterMm: 54, wallThicknessMm: 2.0, internalDiameterMm: 50.0 },
];

/**
 * Looks up a BS EN 1057 copper tube's internal diameter from its outside
 * diameter and table (X or Y). Throws for any size not in the built-in list
 * — supply the internal diameter directly from the manufacturer's own
 * dimensional data rather than guessing at an intermediate size.
 */
export function copperTubeInternalDiameterMm(
  outsideDiameterMm: number,
  table: CopperTubeTable,
): number {
  const rows = table === "X" ? BS_EN_1057_TABLE_X : BS_EN_1057_TABLE_Y;
  const row = rows.find((r) => r.outsideDiameterMm === outsideDiameterMm);
  if (!row) {
    throw new RangeError(
      `No BS EN 1057 Table ${table} entry for a ${outsideDiameterMm}mm OD copper tube in this module's ` +
        `built-in sizes (${rows.map((r) => r.outsideDiameterMm).join(", ")}mm). Supply the internal ` +
        "diameter directly from the manufacturer's own dimensional data instead.",
    );
  }
  return row.internalDiameterMm;
}

interface WaterPropertyRow {
  readonly tempC: number;
  readonly densityKgPerM3: number;
  readonly dynamicViscosityPaS: number;
}

/**
 * CRC Handbook of Chemistry and Physics figures, as tabulated in Wikipedia's
 * "Water (data page)" (viscosity: CRC Handbook; density: Dean & Lange's
 * Handbook of Chemistry, 1999, p.1199), fetched for this work.
 */
const WATER_PROPERTIES_CRC: readonly WaterPropertyRow[] = [
  { tempC: 0, densityKgPerM3: 999.8395, dynamicViscosityPaS: 0.0017921 },
  { tempC: 10, densityKgPerM3: 999.7026, dynamicViscosityPaS: 0.0013077 },
  { tempC: 20, densityKgPerM3: 998.2071, dynamicViscosityPaS: 0.001005 },
  { tempC: 30, densityKgPerM3: 995.6502, dynamicViscosityPaS: 0.0008007 },
  { tempC: 40, densityKgPerM3: 992.21, dynamicViscosityPaS: 0.000656 },
  { tempC: 50, densityKgPerM3: 988.04, dynamicViscosityPaS: 0.0005494 },
  { tempC: 60, densityKgPerM3: 983.21, dynamicViscosityPaS: 0.0004688 },
  { tempC: 70, densityKgPerM3: 977.78, dynamicViscosityPaS: 0.0004061 },
  { tempC: 80, densityKgPerM3: 971.8, dynamicViscosityPaS: 0.0003635 },
];

function round(n: number, dp = 6): number {
  const f = 10 ** dp;
  return Math.round(n * f) / f;
}

/**
 * Water density and dynamic viscosity at a given temperature, linearly
 * interpolated between the CRC Handbook's 10°C steps (see
 * `WATER_PROPERTIES_CRC`). Specific heat is taken as the constant
 * `MIS_3005_D_HOT_WATER` figure already used elsewhere in this package —
 * real water cp varies slightly less than 1% across 0-80°C, and this
 * codebase does not model that temperature dependence anywhere else either.
 * Refuses (does not extrapolate) outside 0-80°C.
 */
export function waterPropertiesAtTempC(tempC: number): FluidProperties {
  if (!Number.isFinite(tempC)) {
    throw new RangeError("tempC must be a finite number.");
  }
  const rows = WATER_PROPERTIES_CRC;
  const firstRow = rows[0];
  const lastRow = rows[rows.length - 1];
  if (!firstRow || !lastRow) {
    throw new Error("WATER_PROPERTIES_CRC table is empty.");
  }
  const minC = firstRow.tempC;
  const maxC = lastRow.tempC;
  if (tempC < minC || tempC > maxC) {
    throw new RangeError(
      `waterPropertiesAtTempC only covers ${minC}-${maxC}°C (CRC Handbook values, via Wikipedia's ` +
        `"Water (data page)"); ${tempC}°C is outside that range. Supply fluid properties directly instead ` +
        "of extrapolating this table.",
    );
  }
  let lower: WaterPropertyRow = firstRow;
  let upper: WaterPropertyRow = lastRow;
  for (let i = 0; i < rows.length - 1; i++) {
    const a = rows[i];
    const b = rows[i + 1];
    if (a && b && tempC >= a.tempC && tempC <= b.tempC) {
      lower = a;
      upper = b;
      break;
    }
  }
  const span = upper.tempC - lower.tempC;
  const fraction = span === 0 ? 0 : (tempC - lower.tempC) / span;
  return {
    densityKgPerM3: round(lower.densityKgPerM3 + (upper.densityKgPerM3 - lower.densityKgPerM3) * fraction, 4),
    dynamicViscosityPaS: round(
      lower.dynamicViscosityPaS + (upper.dynamicViscosityPaS - lower.dynamicViscosityPaS) * fraction,
      8,
    ),
    specificHeatKjPerKgK: MIS_3005_D_HOT_WATER.data.specificHeatKjPerKgK,
  };
}

/**
 * A figure this module needs a design judgement for (a velocity or pressure
 * gradient limit) but which MIS 3005-D does not itself state. Mirrors
 * `GroundLoopFigure` in `ground-loop-sizing.ts`: a `Provenanced<T>` that can
 * additionally be marked `provisional`, meaning it stands in for a
 * confirmed source (CIBSE Guide C, or the heat pump/pipe manufacturer's own
 * guidance) rather than being it.
 */
export interface HydraulicDesignFigure<T> extends Provenanced<T> {
  readonly provisional?: boolean;
  readonly sourceToConfirm?: string;
}

/** See the module doc comment's "PROVISIONAL" section for why this is not a confirmed CIBSE figure. */
export const DEFAULT_MAX_VELOCITY_M_PER_S = 1.0;
/** See the module doc comment's "PROVISIONAL" section — a rough midpoint between conflicting secondary citations. */
export const DEFAULT_MAX_PRESSURE_GRADIENT_PA_PER_M = 300;

const VELOCITY_SOURCE_TO_CONFIRM =
  "Confirm against CIBSE Guide C's own pipe sizing tables, or the heat pump/pipework manufacturer's " +
  "installation guidance, before compliance use. This project has only secondary-source corroboration " +
  "(UK heat pump installer engineering guidance attributing ~1.0 m/s to 'MCS and CIBSE') for the default used here.";

const PRESSURE_GRADIENT_SOURCE_TO_CONFIRM =
  "Confirm against CIBSE Guide C's own pipe sizing tables before compliance use. Secondary sources " +
  "attribute different figures to CIBSE guidance here (readings from ~200 Pa/m up to ~350 Pa/m); the " +
  "default used here is a rough midpoint, not a specific cited CIBSE value.";

function checkFigure(label: string, figure: HydraulicDesignFigure<number>, warnings: CalcWarning[]): void {
  if (figure.provisional) {
    warnings.push({
      code: "PIPE_SIZING_FIGURE_PROVISIONAL",
      severity: "compliance-failure",
      message:
        `The ${label} (${figure.value}) is marked provisional. ` +
        (figure.sourceToConfirm ?? "Confirm the figure against a primary source before compliance use."),
    });
  }
}

export type FlowRegime = "laminar" | "transition" | "turbulent";

export interface PipeSegmentSpec {
  readonly id?: string;
  readonly internalDiameterMm: number;
  /** One-way length of this segment (flow OR return, not both), m. Doubled internally — see the module doc comment. */
  readonly oneWayLengthM: number;
  readonly material: PipeMaterial;
  /** Escape hatch for a roughness this module's built-in table does not cover. mm. */
  readonly roughnessMmOverride?: number;
  /**
   * Equivalent straight-pipe length of this segment's fittings (elbows, tees,
   * valves), already combined for BOTH the flow and return legs, m. A design
   * decision the caller states — this module does not invent a fittings
   * allowance.
   */
  readonly fittingsEquivalentLengthM?: number;
}

export interface PipeSegmentHydraulicResult {
  readonly id?: string;
  readonly internalDiameterMm: number;
  readonly velocityMPerS: number;
  readonly reynoldsNumber: number;
  readonly flowRegime: FlowRegime;
  readonly frictionFactor: number;
  readonly pressureDropPaPerM: number;
  /** This segment's flow leg + return leg + stated fittings, at the design flow rate, Pa. */
  readonly segmentPressureDropPa: number;
}

function frictionFactor(reynolds: number, relativeRoughness: number): { f: number; regime: FlowRegime } {
  if (reynolds < 2300) {
    return { f: 64 / reynolds, regime: "laminar" };
  }
  // Swamee-Jain (1976) explicit approximation to Colebrook-White.
  const term = relativeRoughness / 3.7 + 5.74 / reynolds ** 0.9;
  const f = 0.25 / Math.log10(term) ** 2;
  return { f, regime: reynolds < 5000 ? "transition" : "turbulent" };
}

function sizePipeSegment(spec: PipeSegmentSpec, volumetricFlowM3PerS: number, fluid: FluidProperties): PipeSegmentHydraulicResult {
  if (!(spec.internalDiameterMm > 0)) {
    throw new RangeError("internalDiameterMm must be a positive number.");
  }
  if (!(spec.oneWayLengthM > 0)) {
    throw new RangeError("oneWayLengthM must be a positive number.");
  }
  if (spec.fittingsEquivalentLengthM !== undefined && spec.fittingsEquivalentLengthM < 0) {
    throw new RangeError("fittingsEquivalentLengthM must not be negative.");
  }

  const dM = spec.internalDiameterMm / 1000;
  const areaM2 = (Math.PI * dM ** 2) / 4;
  const velocityMPerS = volumetricFlowM3PerS / areaM2;

  const roughnessMm = spec.roughnessMmOverride ?? PIPE_MATERIAL_ROUGHNESS_MM[spec.material];
  const relativeRoughness = roughnessMm / 1000 / dM;

  const reynoldsNumber = (fluid.densityKgPerM3 * velocityMPerS * dM) / fluid.dynamicViscosityPaS;
  const { f, regime } = frictionFactor(reynoldsNumber, relativeRoughness);

  const pressureDropPaPerM = (f * (fluid.densityKgPerM3 * velocityMPerS ** 2)) / (2 * dM);
  const totalLengthM = 2 * spec.oneWayLengthM + (spec.fittingsEquivalentLengthM ?? 0);
  const segmentPressureDropPa = pressureDropPaPerM * totalLengthM;

  return {
    id: spec.id,
    internalDiameterMm: spec.internalDiameterMm,
    velocityMPerS: round(velocityMPerS, 4),
    reynoldsNumber: round(reynoldsNumber, 1),
    flowRegime: regime,
    frictionFactor: round(f, 6),
    pressureDropPaPerM: round(pressureDropPaPerM, 2),
    segmentPressureDropPa: round(segmentPressureDropPa, 1),
  };
}

export interface IndexCircuitSizingInput {
  /** The index circuit's design heat load, W. */
  readonly designHeatLoadW: number;
  /** Design flow-minus-return temperature difference, K — a design decision (commonly 5-10K on a heat pump primary circuit). */
  readonly designFlowReturnDeltaTK: number;
  /** The index circuit modelled as one or more pipe segments in series, in flow direction. At least one required. */
  readonly segments: readonly PipeSegmentSpec[];
  /** Mean fluid temperature the flow rate/properties are evaluated at, °C. */
  readonly meanFluidTempC: number;
  /** Fluid properties override — supply the output of `glycolFluidProperties` (glycol.ts) here for an antifreeze-loaded circuit. Defaults to water at `meanFluidTempC`. */
  readonly fluid?: FluidProperties;
  readonly maxVelocityMPerS?: HydraulicDesignFigure<number>;
  readonly maxPressureGradientPaPerM?: HydraulicDesignFigure<number>;
  readonly complianceMode: boolean;
  readonly calculationDate: string;
}

export interface IndexCircuitSizingResult {
  readonly designMassFlowRateKgPerS: number;
  readonly designFlowRateLitresPerMin: number;
  readonly fluidUsed: FluidProperties;
  readonly fluidSource: "water-table" | "supplied";
  readonly segments: readonly PipeSegmentHydraulicResult[];
  readonly peakVelocityMPerS: number;
  readonly maxVelocityMPerS: number;
  readonly velocityWithinLimit: boolean;
  readonly peakPressureGradientPaPerM: number;
  readonly maxPressureGradientPaPerM: number;
  readonly pressureGradientWithinLimit: boolean;
  /** The index circuit's total pressure drop at the design flow rate — the circulator's duty point. */
  readonly circulatorDutyPressureDropPa: number;
  readonly circulatorDutyPressureDropKPa: number;
  readonly circulatorDutyHeadM: number;
  readonly working: readonly string[];
  readonly warnings: readonly CalcWarning[];
  readonly stamp: CalcStamp;
}

/** Standard gravity, m/s² — exact by definition (ISO 80000-3), not a looked-up figure. */
const STANDARD_GRAVITY_M_PER_S2 = 9.80665;

/**
 * Sizes an index (primary) heating circuit: design flow rate from the heat
 * load and design ΔT, velocity and Darcy-Weisbach pressure drop through each
 * supplied pipe segment, and the resulting circulator duty point. See the
 * module doc comment for the method and its citations.
 */
export function calculateIndexCircuitSizing(input: IndexCircuitSizingInput): IndexCircuitSizingResult {
  if (!(input.designHeatLoadW > 0) || !Number.isFinite(input.designHeatLoadW)) {
    throw new RangeError("designHeatLoadW must be a positive number.");
  }
  if (!(input.designFlowReturnDeltaTK > 0)) {
    throw new RangeError("designFlowReturnDeltaTK must be a positive number.");
  }
  if (input.segments.length === 0) {
    throw new RangeError("At least one pipe segment is required to size an index circuit.");
  }

  const warnings: CalcWarning[] = [];
  const working: string[] = [];

  const fluid = input.fluid ?? waterPropertiesAtTempC(input.meanFluidTempC);
  const fluidSource: IndexCircuitSizingResult["fluidSource"] = input.fluid ? "supplied" : "water-table";

  const designLoadKW = input.designHeatLoadW / 1000;
  const designMassFlowRateKgPerS = designLoadKW / (fluid.specificHeatKjPerKgK * input.designFlowReturnDeltaTK);
  const volumetricFlowM3PerS = designMassFlowRateKgPerS / fluid.densityKgPerM3;
  const designFlowRateLitresPerMin = volumetricFlowM3PerS * 1000 * 60;

  working.push(
    `Mass flow = ${designLoadKW} kW / (${fluid.specificHeatKjPerKgK} kJ/(kg·K) × ${input.designFlowReturnDeltaTK} K) = ${round(designMassFlowRateKgPerS, 5)} kg/s`,
    `Volumetric flow = ${round(designMassFlowRateKgPerS, 5)} kg/s / ${fluid.densityKgPerM3} kg/m³ = ${round(designFlowRateLitresPerMin, 3)} L/min`,
  );

  const segments = input.segments.map((spec) => sizePipeSegment(spec, volumetricFlowM3PerS, fluid));

  const peakVelocityMPerS = Math.max(...segments.map((s) => s.velocityMPerS));
  const peakPressureGradientPaPerM = Math.max(...segments.map((s) => s.pressureDropPaPerM));
  const circulatorDutyPressureDropPa = round(
    segments.reduce((sum, s) => sum + s.segmentPressureDropPa, 0),
  );

  let maxVelocityFigure: HydraulicDesignFigure<number>;
  if (input.maxVelocityMPerS !== undefined) {
    maxVelocityFigure = input.maxVelocityMPerS;
    checkFigure("maximum pipe velocity", maxVelocityFigure, warnings);
  } else {
    maxVelocityFigure = {
      value: DEFAULT_MAX_VELOCITY_M_PER_S,
      provenance: "derived",
      provisional: true,
      sourceToConfirm: VELOCITY_SOURCE_TO_CONFIRM,
    };
    warnings.push({
      code: "PIPE_SIZING_FIGURE_PROVISIONAL",
      severity: "compliance-failure",
      message:
        `No maximum pipe velocity was supplied, so the provisional default of ${DEFAULT_MAX_VELOCITY_M_PER_S} m/s ` +
        `was used. ${VELOCITY_SOURCE_TO_CONFIRM}`,
    });
  }

  let maxPressureGradientFigure: HydraulicDesignFigure<number>;
  if (input.maxPressureGradientPaPerM !== undefined) {
    maxPressureGradientFigure = input.maxPressureGradientPaPerM;
    checkFigure("maximum pressure gradient", maxPressureGradientFigure, warnings);
  } else {
    maxPressureGradientFigure = {
      value: DEFAULT_MAX_PRESSURE_GRADIENT_PA_PER_M,
      provenance: "derived",
      provisional: true,
      sourceToConfirm: PRESSURE_GRADIENT_SOURCE_TO_CONFIRM,
    };
    warnings.push({
      code: "PIPE_SIZING_FIGURE_PROVISIONAL",
      severity: "compliance-failure",
      message:
        `No maximum pressure gradient was supplied, so the provisional default of ${DEFAULT_MAX_PRESSURE_GRADIENT_PA_PER_M} Pa/m ` +
        `was used. ${PRESSURE_GRADIENT_SOURCE_TO_CONFIRM}`,
    });
  }

  const velocityWithinLimit = peakVelocityMPerS <= maxVelocityFigure.value;
  if (!velocityWithinLimit) {
    warnings.push({
      code: "PIPE_VELOCITY_EXCEEDS_LIMIT",
      severity: "compliance-failure",
      message:
        `Peak velocity (${peakVelocityMPerS} m/s) exceeds the ${maxVelocityFigure.value} m/s limit — ` +
        "increase the pipe diameter for the segment(s) at or near this velocity, or reduce the design ΔT.",
    });
  }

  const pressureGradientWithinLimit = peakPressureGradientPaPerM <= maxPressureGradientFigure.value;
  if (!pressureGradientWithinLimit) {
    warnings.push({
      code: "PIPE_PRESSURE_GRADIENT_EXCEEDS_LIMIT",
      severity: "compliance-failure",
      message:
        `Peak pressure gradient (${peakPressureGradientPaPerM} Pa/m) exceeds the ${maxPressureGradientFigure.value} Pa/m ` +
        "limit — increase the pipe diameter for the segment(s) driving this figure.",
    });
  }

  for (const s of segments) {
    if (s.flowRegime === "laminar") {
      warnings.push({
        code: "PIPE_SEGMENT_LAMINAR_FLOW",
        severity: "info",
        message:
          `Segment ${s.id ?? s.internalDiameterMm + "mm"} is in laminar flow (Re=${s.reynoldsNumber}). Unusual ` +
          "for a heating primary circuit at design flow — check the flow rate and pipe size are as intended.",
      });
    } else if (s.flowRegime === "transition") {
      warnings.push({
        code: "PIPE_SEGMENT_TRANSITION_FLOW",
        severity: "info",
        message:
          `Segment ${s.id ?? s.internalDiameterMm + "mm"} is in the transition zone (Re=${s.reynoldsNumber}). No ` +
          "single friction-factor correlation is well established there; the Swamee-Jain figure used is an " +
          "approximation outside its stated validity range (Re >= 5000).",
      });
    }
  }

  warnings.push(
    {
      code: "PIPE_NETWORK_NOT_MODELLED",
      severity: "info",
      message:
        "This sizes only the single index circuit run supplied, as a series of pipe segments. It does not " +
        "model or balance a branched pipework network — every other branch is assumed to be no more " +
        "restrictive than this one, which is the standard index-circuit assumption but is not itself checked here.",
    },
    {
      code: "PIPE_COMPONENT_LOSSES_NOT_INCLUDED",
      severity: "info",
      message:
        "Component pressure losses (the heat pump's own internal head loss, plate heat exchangers, buffer " +
        "vessels, zone valves, TRVs, strainers) are not included unless folded into a segment's " +
        "fittingsEquivalentLengthM by the caller. Add the manufacturer's stated losses for these separately " +
        "before sizing the circulator from this figure alone.",
    },
  );

  if (input.complianceMode) {
    const failures = warnings.filter((w) => w.severity === "compliance-failure");
    if (failures.length > 0) {
      throw new ComplianceError("Index circuit pipe sizing cannot be issued in compliance mode.", failures);
    }
  }

  return {
    designMassFlowRateKgPerS: round(designMassFlowRateKgPerS, 5),
    designFlowRateLitresPerMin: round(designFlowRateLitresPerMin, 3),
    fluidUsed: fluid,
    fluidSource,
    segments,
    peakVelocityMPerS,
    maxVelocityMPerS: maxVelocityFigure.value,
    velocityWithinLimit,
    peakPressureGradientPaPerM,
    maxPressureGradientPaPerM: maxPressureGradientFigure.value,
    pressureGradientWithinLimit,
    circulatorDutyPressureDropPa,
    circulatorDutyPressureDropKPa: round(circulatorDutyPressureDropPa / 1000, 4),
    circulatorDutyHeadM: round(circulatorDutyPressureDropPa / (fluid.densityKgPerM3 * STANDARD_GRAVITY_M_PER_S2), 4),
    working,
    warnings,
    stamp: {
      engineVersion: ENGINE_VERSION,
      rulesBundleVersion: RULES_BUNDLE_VERSION,
      calculatedAt: input.calculationDate,
      complianceMode: input.complianceMode,
    },
  };
}
